<template>
  <div class="dietbreakdown">
    <div class="dietbreakdown__navbar">
      <button @click="resetQuiz" :class="{'hidden': this.$store.state.menuShown}">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.00001 0.999988L2 7L8.00001 13" stroke="#FF7D26" stroke-width="2"/>
        </svg>
        <span>Retake Quiz</span>
      </button>

      <GlobalNav/>
    </div>

    <div class="container">
      <h1>Your Environmental Impact Score:</h1>
      <h2 class="score-h2">{{ this.userImpactScore.title }}</h2>

      <div class="diet-score">
        <el-slider
          v-model="userImpactScore.score"
          :step="1"
          :min="1"
          :max="5"
          height="14"
          :disabled="true"
          :show-tooltip="false"
          class="scorecard"
          >
        </el-slider>
        <div class="diet-score__labels">
          <div class="label" v-for="(label, index) in this.impactScore" :key="index" v-html="label.scoreTitle" :class="{ 'active': label.scoreTitle == userImpactScore.scoreTitle }"></div>
        </div>
      </div>

      <div class="score__explanation">
        <div class="score__title">{{ userImpactScore.scoreHeading }}</div>
        <p>{{ userImpactScore.scoreSummary }}</p>
      </div>

      <RadarChart2 :data="this.radarData" />

      <div class="planetary">
        <div class="heading">The Planetary Health Diet</div>
        <p>The EAT-Lancet Commission, consisting of leading nutrition and environmental scientists, developed an optimal diet that is healthy for both people and planet. But their recommendations aren’t set in stone -- they provide ranges within different food groups to accommodate accessibility, cultural traditions, and personal preferences. A planetary health plate should consist by volume of approximately half a plate of vegetables and fruits; the other half, displayed by contribution to calories, should consist of primarily whole grains, plant protein sources, unsaturated plant oils, and (optionally) modest amounts of animal sources of protein.</p>

        <div class="pie-chart">
          <img src="../assets/planetary-health-diet.svg" alt="">
        </div>

        <!-- <div class="chart-sub-label">
          *Includes the categories <strong>Added sugars</strong> and <strong>Starchy vegetables</strong>
        </div> -->

        <p>Should you choose to consume animal products, it is important to seek out those that ensure a meaningful baseline of animal welfare, like cage-free eggs. If you decide to keep your plate purely plant-based, make sure you’re including foods packed with omega-3 fatty acids and a supplement of vitamin B12.</p>
      </div>
    </div>

    <div class="dietbreakdown__bottom">
      <SuggestedRecipes :suggestedMeals="this.suggestedMeals" />
      <CTA />
    </div>
  </div>
</template>

<script>
import mealsData from '../data/meals.json'
import idealDiets from '../data/idealDiets.json'
import userWeightedImpact from '../data/userWeightedImpact.json'
import impactMultipliersData from '../data/ImpactMultipliers.json'

import RadarChart2 from '@/components/RadarChart2'
import GlobalNav from '@/components/GlobalNav'
import SuggestedRecipes from '@/components/SuggestedRecipes'
import CTA from '@/components/CTA'

import { Slider } from 'element-ui'

export default {
  name: 'DietBreakdown',
  metaInfo() {
   // if no subcomponents specify a metaInfo.title, this title will be used
   return {
     title: 'Your environmental impact'
   }
  },
  components: {
    RadarChart2,
    GlobalNav,
    'el-slider': Slider,
    SuggestedRecipes,
    CTA
  },
  props: {

  },
  data() {
    return {
      loading: false,
      mealBreakdown: null,
      impactScore: [
        { scoreTitle: 'Planet<br/> hero!', title: 'Planet hero!', scoreHeading: 'You’re leading the way towards a sustainable future.', scoreSummary: 'Here’s how your diet compares to the ideal diet recommended by the EAT-Lancet Commission. Select an impact category to view more details.', score: 5 },
        { scoreTitle: 'Not </br>bad!', title: 'Not bad!', scoreHeading: 'See how you can do more for the planet below.', scoreSummary: 'Here’s how your diet compares to the ideal diet recommended by the EAT-Lancet Commission. Select an impact category to view more details.', score: 4 },
        { scoreTitle: 'So-so.', title: 'So-so.', scoreHeading: 'Let’s step it up!', scoreSummary: 'Here’s how your diet compares to the ideal diet recommended by the EAT-Lancet Commission. Select an impact category to view more details.', score: 3 },
        { scoreTitle: 'Needs some<br/> work.', title: 'Needs some work.', scoreHeading: `There's room to improve--let's get started!`, scoreSummary: 'Here’s how your diet compares to the ideal diet recommended by the EAT-Lancet Commission. Select an impact category to view more details.', score: 2 },
        { scoreTitle: 'Yikes!', title: 'Yikes!', scoreHeading: 'Our food future is unsustainable this way.', scoreSummary: 'Here’s how your diet compares to the ideal diet recommended by the EAT-Lancet Commission. Select an impact category to view more details.', score: 1 }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  mounted() {
    this.$parent.$refs.quiz.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetchData()
  },
  beforeDestroy() {
    if(this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    fetchData() {
      this.error = this.mealBreakdown = null
      this.loading = true
    },
    resetQuiz() {
      this.$store.dispatch('DietImpact/resetQuizState', null, {root: true})
      // this.$router.push({ name: 'evaluate-your-plate' })
    }
  },
  computed: {
    region() {
      return this.$store.state.Region.region
    },
    meals() {
      return mealsData[this.region]
    },
    idealDiet() {
      if (!this.userEatsMeat && !this.userEatsEggsOrDairy) { // Vegan - NO Meat, Eggs, or dairy
        return idealDiets[this.region]['Vegan Diet']
      } else if (!this.userEatsMeat && this.userEatsEggsOrDairy) { // Vegetarian meals only (NO Meat, Can have Eggs & Dairy)
        return idealDiets[this.region]['Vegetarian Diet']
      }
      // User eats meals w/ Meat & Eggs & Dairy
      return idealDiets[this.region]['Ideal Diet']
    },
    impactMultipliers() {
      return impactMultipliersData[this.region]
    },
    questionData() {
      return this.$store.state.DietImpact.questions
    },
    meatTotals() {
      let arr = this.questionData[0].questions[0]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    dairyTotals() {
      let arr = this.questionData[0].questions[1]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    eggsTotal() {
      let arr = this.questionData[0].questions[2]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    fruitsTotal() {
      let arr = this.questionData[0].questions[3]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    vegetablesTotal() {
      let arr = this.questionData[0].questions[4]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    grainsTotal() {
      let arr = this.questionData[0].questions[5]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    nutsTotal() {
      let arr = this.questionData[0].questions[6]
      var count = 0
      // For each meal time selected (i.e. breakfast, lunch, dinner etc)
      for (var i = 0; i < arr.selected.length; i++) {
        let selected = arr.selected[i]
        // Get selected meal time's value
        for (var k = 0; k < arr.options.length; k++) {
          if (arr.options[k].label.includes(selected)) {
            count += arr.options[k].val
          }
        }
      }
      return count
    },
    totalFoodGroups() {
      return (this.meatTotals * this.meatMultiplier) + this.dairyTotals + this.eggsTotal + this.fruitsTotal + this.vegetablesTotal + this.grainsTotal + this.nutsTotal
    },
    totalFoodGroupsWithOils() {
      return this.totalFoodGroups / (1 - this.percentageOfOils)
    },
    percentageOfOils() {
      if (!this.userEatsMeat && !this.userEatsEggsOrDairy) { // Vegan - NO Meat, Eggs, or dairy
        return 0.0276
      } else if (!this.userEatsMeat && this.userEatsEggsOrDairy) { // Vegetarian
        return 0.0234
      }
      return 0.0313
    },
    percentageOfMeat() {
      return (this.meatTotals * this.meatMultiplier / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfDairy() {
      return (this.dairyTotals / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfEggs() {
      return (this.eggsTotal / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfFruits() {
      return (this.fruitsTotal / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfVegetables() {
      return (this.vegetablesTotal / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfGrains() {
      return (this.grainsTotal / this.totalFoodGroupsWithOils).toFixed(2)
    },
    percentageOfNuts() {
      return (this.nutsTotal / this.totalFoodGroupsWithOils).toFixed(2)
    },
    userEatsMeat() {
      return this.meatTotals > 0 ? true : false
    },
    userEatsEggsOrDairy() { // Check for potential vegan
      return this.eggsTotal > 0 || this.dairyTotals > 0 ? true : false
    },
    meatMultiplier() {
      return this.questionData[1].value > 0 ? this.questionData[1].value : 0.5
    },
    beefPercent() {
      return this.questionData[2].options[0].value * this.percentageOfMeat
    },
    porkPercent() {
      return this.questionData[2].options[1].value * this.percentageOfMeat
    },
    chickenPercent() {
      return this.questionData[2].options[2].value * this.percentageOfMeat
    },
    fishPercent() {
      return this.questionData[2].options[3].value * this.percentageOfMeat
    },
    beefData() {
      return this.impactMultipliers.slice().find(product => product.Group.includes('Beef'))
    },
    porkData() {
      return this.impactMultipliers.slice().find(product => product.Group.includes('Pork'))
    },
    chickenData() {
      return this.impactMultipliers.slice().find(product => product.Group.includes('Poultry'))
    },
    seafoodData() {
      return this.impactMultipliers.slice().find(product => product.Group.includes('Seafood'))
    },
    dairyData() {
      return this.impactMultipliers.find(product => product.Group.includes('Dairy'))
    },
    eggsData() {
      return this.impactMultipliers.find(product => product.Group.includes('Eggs'))
    },
    fruitsData() {
      return this.impactMultipliers.find(product => product.Group.includes('Fruits'))
    },
    vegetablesData() {
      return this.impactMultipliers.find(product => product.Group.includes('Vegetables'))
    },
    grainsData() {
      return this.impactMultipliers.find(product => product.Group.includes('Grains'))
    },
    nutsData() {
      return this.impactMultipliers.find(product => product.Group.includes('Nuts'))
    },
    userWeightedImpactData() {
      return userWeightedImpact[this.region]
    },
    userDietImpact() {
      let userBeef = {
        'GHG': this.beefData['GHGs (kg CO2e/kg)'] * this.beefPercent,
        'Nutrient': this.beefData['Eutrophying Emissions (gPO43-eq/kg)'] * this.beefPercent,
        'Biodiversity': this.beefData['Biodiversity Loss (Species*yr/kg)'] * this.beefPercent,
        'Animal': this.beefData['Animal Equivalency (animals impacted/kg)'] * this.beefPercent,
        'Water': this.beefData['Stress-Weighted Water Use (L/kg)'] * this.beefPercent,
        'Land': this.beefData['Total land use (m2/kg)'] * this.beefPercent,
        'WeightedImpact': this.beefData['Weighted Total Impact (Total)'] * this.beefPercent
      }

      let userPork = {
        'GHG': this.porkData['GHGs (kg CO2e/kg)'] * this.porkPercent,
        'Nutrient': this.porkData['Eutrophying Emissions (gPO43-eq/kg)'] * this.porkPercent,
        'Biodiversity': this.porkData['Biodiversity Loss (Species*yr/kg)'] * this.porkPercent,
        'Animal': this.porkData['Animal Equivalency (animals impacted/kg)'] * this.porkPercent,
        'Water': this.porkData['Stress-Weighted Water Use (L/kg)'] * this.porkPercent,
        'Land': this.porkData['Total land use (m2/kg)'] * this.porkPercent,
        'WeightedImpact': this.porkData['Weighted Total Impact (Total)'] * this.porkPercent
      }

      let userChicken = {
        'GHG': this.chickenData['GHGs (kg CO2e/kg)'] * this.chickenPercent,
        'Nutrient': this.chickenData['Eutrophying Emissions (gPO43-eq/kg)'] * this.chickenPercent,
        'Biodiversity': this.chickenData['Biodiversity Loss (Species*yr/kg)'] * this.chickenPercent,
        'Animal': this.chickenData['Animal Equivalency (animals impacted/kg)'] * this.chickenPercent,
        'Water': this.chickenData['Stress-Weighted Water Use (L/kg)'] * this.chickenPercent,
        'Land': this.chickenData['Total land use (m2/kg)'] * this.chickenPercent,
        'WeightedImpact': this.chickenData['Weighted Total Impact (Total)'] * this.chickenPercent
      }

      let userSeafood = {
        'GHG': this.seafoodData['GHGs (kg CO2e/kg)'] * this.fishPercent,
        'Nutrient': this.seafoodData['Eutrophying Emissions (gPO43-eq/kg)'] * this.fishPercent,
        'Biodiversity': this.seafoodData['Biodiversity Loss (Species*yr/kg)'] * this.fishPercent,
        'Animal': this.seafoodData['Animal Equivalency (animals impacted/kg)'] * this.fishPercent,
        'Water': this.seafoodData['Stress-Weighted Water Use (L/kg)'] * this.fishPercent,
        'Land': this.seafoodData['Total land use (m2/kg)'] * this.fishPercent,
        'WeightedImpact': this.seafoodData['Weighted Total Impact (Total)'] * this.fishPercent
      }

      let userDairy = {
        'GHG': this.dairyData['GHGs (kg CO2e/kg)'] * this.percentageOfDairy,
        'Nutrient': this.dairyData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfDairy,
        'Biodiversity': this.dairyData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfDairy,
        'Animal': this.dairyData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfDairy,
        'Water': this.dairyData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfDairy,
        'Land': this.dairyData['Total land use (m2/kg)'] * this.percentageOfDairy,
        'WeightedImpact': this.dairyData['Weighted Total Impact (Total)'] * this.percentageOfDairy
      }

      let userEggs = {
        'GHG': this.eggsData['GHGs (kg CO2e/kg)'] * this.percentageOfEggs,
        'Nutrient': this.eggsData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfEggs,
        'Biodiversity': this.eggsData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfEggs,
        'Animal': this.eggsData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfEggs,
        'Water': this.eggsData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfEggs,
        'Land': this.eggsData['Total land use (m2/kg)'] * this.percentageOfEggs,
        'WeightedImpact': this.eggsData['Weighted Total Impact (Total)'] * this.percentageOfEggs
      }

      let userFruits = {
        'GHG': this.fruitsData['GHGs (kg CO2e/kg)'] * this.percentageOfFruits,
        'Nutrient': this.fruitsData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfFruits,
        'Biodiversity': this.fruitsData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfFruits,
        'Animal': this.fruitsData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfFruits,
        'Water': this.fruitsData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfFruits,
        'Land': this.fruitsData['Total land use (m2/kg)'] * this.percentageOfFruits,
        'WeightedImpact': this.fruitsData['Weighted Total Impact (Total)'] * this.percentageOfFruits
      }

      let userVegetables = {
        'GHG': this.vegetablesData['GHGs (kg CO2e/kg)'] * this.percentageOfVegetables,
        'Nutrient': this.vegetablesData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfVegetables,
        'Biodiversity': this.vegetablesData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfVegetables,
        'Animal': this.vegetablesData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfVegetables,
        'Water': this.vegetablesData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfVegetables,
        'Land': this.vegetablesData['Total land use (m2/kg)'] * this.percentageOfVegetables,
        'WeightedImpact': this.vegetablesData['Weighted Total Impact (Total)'] * this.percentageOfVegetables
      }

      let userGrains = {
        'GHG': this.grainsData['GHGs (kg CO2e/kg)'] * this.percentageOfGrains,
        'Nutrient': this.grainsData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfGrains,
        'Biodiversity': this.grainsData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfGrains,
        'Animal': this.grainsData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfGrains,
        'Water': this.grainsData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfGrains,
        'Land': this.grainsData['Total land use (m2/kg)'] * this.percentageOfGrains,
        'WeightedImpact': this.grainsData['Weighted Total Impact (Total)'] * this.percentageOfGrains
      }

      let userNuts = {
        'GHG': this.nutsData['GHGs (kg CO2e/kg)'] * this.percentageOfNuts,
        'Nutrient': this.nutsData['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfNuts,
        'Biodiversity': this.nutsData['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfNuts,
        'Animal': this.nutsData['Animal Equivalency (animals impacted/kg)'] * this.percentageOfNuts,
        'Water': this.nutsData['Stress-Weighted Water Use (L/kg)'] * this.percentageOfNuts,
        'Land': this.nutsData['Total land use (m2/kg)'] * this.percentageOfNuts,
        'WeightedImpact': this.nutsData['Weighted Total Impact (Total)'] * this.percentageOfNuts
      }

      let userOils = {
        'GHG': this.impactMultipliers.find(a => a.Group.includes('Oils'))['GHGs (kg CO2e/kg)'] * this.percentageOfOils,
        'Nutrient': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Eutrophying Emissions (gPO43-eq/kg)'] * this.percentageOfOils,
        'Biodiversity': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Biodiversity Loss (Species*yr/kg)'] * this.percentageOfOils,
        'Animal': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Animal Equivalency (animals impacted/kg)'] * this.percentageOfOils,
        'Water': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Stress-Weighted Water Use (L/kg)'] * this.percentageOfOils,
        'Land': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Total land use (m2/kg)'] * this.percentageOfOils,
        'WeightedImpact': this.impactMultipliers.find(a => a.Group.includes('Oils'))['Weighted Total Impact (Total)'] * this.percentageOfOils
      }

      let userImpact = {
        'GHG': userBeef.GHG + userPork.GHG + userChicken.GHG + userSeafood.GHG + userDairy.GHG + userEggs.GHG + userFruits.GHG + userVegetables.GHG + userGrains.GHG + userNuts.GHG + userOils.GHG,
        'Nutrient': userBeef.Nutrient + userPork.Nutrient + userChicken.Nutrient + userSeafood.Nutrient + userDairy.Nutrient + userEggs.Nutrient + userFruits.Nutrient + userVegetables.Nutrient + userGrains.Nutrient + userNuts.Nutrient + userOils.Nutrient,
        'Biodiversity': userBeef.Biodiversity + userPork.Biodiversity + userChicken.Biodiversity + userSeafood.Biodiversity + userDairy.Biodiversity + userEggs.Biodiversity + userFruits.Biodiversity + userVegetables.Biodiversity + userGrains.Biodiversity + userNuts.Biodiversity + userOils.Biodiversity,
        'Animal': userBeef.Animal + userPork.Animal + userChicken.Animal + userSeafood.Animal + userDairy.Animal + userEggs.Animal + userFruits.Animal + userVegetables.Animal + userGrains.Animal + userNuts.Animal + userOils.Animal,
        'Water': userBeef.Water + userPork.Water + userChicken.Water + userSeafood.Water + userDairy.Water + userEggs.Water + userFruits.Water + userVegetables.Water + userGrains.Water + userNuts.Water + userOils.Water,
        'Land': userBeef.Land + userPork.Land + userChicken.Land + userSeafood.Land + userDairy.Land + userEggs.Land + userFruits.Land + userVegetables.Land + userGrains.Land + userNuts.Land + userOils.Land,
        'WeightedImpact': userBeef.WeightedImpact + userPork.WeightedImpact + userChicken.WeightedImpact + userSeafood.WeightedImpact + userDairy.WeightedImpact + userEggs.WeightedImpact + userFruits.WeightedImpact + userVegetables.WeightedImpact + userGrains.WeightedImpact + userNuts.WeightedImpact + userOils.WeightedImpact
      }
      return userImpact
    },
    userImpactScore() {
      let x = this.userDietImpact.WeightedImpact

      if (this.region == 'NA') {
        return (x <= 3) ? this.impactScore[0] : // 'A'
               (x <= 3.5) ? this.impactScore[1] : // 'B'
               (x <= 4.1) ? this.impactScore[2] : // 'C'
               (x <= 5) ? this.impactScore[3] : this.impactScore[4] // 'D' else 'F'
      } else { // EU
        return (x <= 4) ? this.impactScore[0] : // 'A'
               (x <= 6.1) ? this.impactScore[1] : // 'B'
               (x <= 8) ? this.impactScore[2] : // 'C'
               (x <= 10) ? this.impactScore[3] : this.impactScore[4] // 'D' else 'F'
      }
    },
    planetaryHealthDietImpact() {
      let x = this.idealDiet.find(a => a.Group.includes('SUM'))
      let data = {
        'GHG': x['GHGs (kg CO2e/kg)'],
        'Nutrient': x['Eutrophying Emissions (gPO43-eq/kg)'],
        'Biodiversity': x['Biodiversity Loss (Species*yr/kg)'],
        'Animal': x['Animal Equivalency (animals impacted/kg)'],
        'Water': x['Stress-Weighted Water Use (L/kg)'],
        'Land': x['Total land use (m2/kg)'],
        'WeightedImpact': x['Weighted Total Impact (Total)']
      }
      return data
    },
    radarData() {
      let data = []
      data.push(
        {'Impacts': 'Greenhouse Gases', 'val': this.userDietImpact.GHG, 'val2': this.planetaryHealthDietImpact.GHG, 'icon': 'icon-info.svg'},
        {'Impacts': 'Nutrient Pollution', 'val': this.userDietImpact.Nutrient, 'val2': this.planetaryHealthDietImpact.Nutrient, 'icon': 'icon-info.svg'},
        {'Impacts': 'Biodiversity Loss', 'val': this.userDietImpact.Biodiversity, 'val2': this.planetaryHealthDietImpact.Biodiversity, 'icon': 'icon-info.svg'},
        {'Impacts': 'Animal Impact', 'val': this.userDietImpact.Animal, 'val2': this.planetaryHealthDietImpact.Animal, 'icon': 'icon-info.svg'},
        {'Impacts': 'Water Use', 'val': this.userDietImpact.Water, 'val2': this.planetaryHealthDietImpact.Water, 'icon': 'icon-info.svg'},
        {'Impacts': 'Land Use', 'val': this.userDietImpact.Land, 'val2': this.planetaryHealthDietImpact.Land, 'icon': 'icon-info.svg'}
      )
      return data
    },
    veganMeals() {
      return this.meals.filter(meals => meals['Vegan Y/N'].toLowerCase().includes('y'))
    },
    vegetarianMeals() {
      return this.meals.filter(meals => meals['Vegetarian Y/N'].toLowerCase().includes('y'))
    },
    suggestedMeals() {
      return this.sortedByflavorProfile
    },
    lowImpactMeals() { // Return meals < 1.50E-12 Weighted Impact
      let threshold = this.region == 'NA' ? 1.33E-12 : 1.0E-12

      if (!this.userEatsMeat && !this.userEatsEggsOrDairy) { // Vegan - NO Meat, Eggs, or dairy
        return this.veganMeals.slice().filter(meals => meals['Weighted Impact'] <= threshold)
      } else if (!this.userEatsMeat && this.userEatsEggsOrDairy) { // Vegetarian meals only (NO Meat, Can have Eggs & Dairy)
        return this.vegetarianMeals.slice().filter(meals => meals['Weighted Impact'] <= threshold)
      } else { // Include meals w/ Meat & Eggs & Dairy
        return this.meals.slice().filter(meals => meals['Weighted Impact'] <= threshold)
      }
    },
    sweetnessProfile() {
      return this.$store.state.DietImpact.questions[3].questions[0].value
    },
    spiceProfile() {
      return this.$store.state.DietImpact.questions[3].questions[1].value
    },
    flavorProfile() {
      return this.$store.state.DietImpact.questions[3].questions[2].value
    },
    sortedByflavorProfile() {
      return this.lowImpactMeals.slice().sort((a, b) => {
        let sweetnessDiffA = Math.abs(this.sweetnessProfile - a['Sweetness (0-4)'])
        let SpicyDiffA = Math.abs(this.spiceProfile - a['Spicy (0-4)'])
        let flavorDiffA = Math.abs(this.flavorProfile - a['Flavor (0-4)'])
        let cumulativeDiffA = sweetnessDiffA + SpicyDiffA + flavorDiffA

        let sweetnessDiffB = Math.abs(this.sweetnessProfile - b['Sweetness (0-4)'])
        let SpicyDiffB = Math.abs(this.spiceProfile - b['Spicy (0-4)'])
        let flavorDiffB = Math.abs(this.flavorProfile - b['Flavor (0-4)'])
        let cumulativeDiffB = sweetnessDiffB + SpicyDiffB + flavorDiffB

        if (cumulativeDiffA < cumulativeDiffB) {
          return -1
        }
        if (cumulativeDiffA > cumulativeDiffB) {
          return 1
        }
        return 0
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
@import '../assets/styles/vars';
  .dietbreakdown {
    // margin-top: 40px;
    @media (min-width: 768px) {
      // box-shadow: 0px 1px 20px rgba(0, 0, 0, 0.2);
    }
    h1 {
      color: #42464F;
      font-family: $fSecondary;
      font-weight: $fwReg;
      font-size: 26px;
      line-height: 39px;
      margin-bottom: 20px;
      text-align: center;
    }
    h2.score-h2 {
      color: #42464F;
      font-family: $fSecondary;
      font-weight: $fwReg;
      font-size: 20px;
      line-height: 28px;
      margin-bottom: 20px;
      text-transform: uppercase;
      text-align: center;
    }
    .container {
      padding-left: 24px;
      padding-right: 24px;
      padding-top: 40px;
      @media (min-width: 768px) {
        padding-left: 40px;
        padding-right: 40px;
      }
    }
    &__navbar {
      position: sticky;
      top: 0;
      width: 100%;
      background: $cWhite;
      border-bottom: 1px solid $cGray;
      padding-left: 24px;
      padding-right: 24px;
      padding-top: 20px;
      padding-bottom: 20px;
      z-index: 99;
      display: flex;
      flex-flow: row;
      justify-content: space-between;
      button {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: $cBlack;
        background: none;
        border: none;
        cursor: pointer;
        span {
          font-family: $fPrimary;
          font-weight: $fwSemiBold;
          font-size: 14px;
          line-height: 17px;
          padding-left: 8px;
        }
      }
    }

    .diet-score {
      position: relative;
      &__labels {
        display: flex;
        flex-flow: row-reverse;
        justify-content: space-between;
        .label{
          font-family: $fPrimary;
          font-weight: $fwSemiBold;
          font-size: 10px;
          line-height: 12px;
          color: #A2A2A2;
          &.active {
            color: $cBlack;
          }
          &:nth-of-type(3) {
            text-align: center;
            margin-right: 30px;
          }
        }
      }

    }
    &__bottom {
      background: $cLightBG;
      padding-top: 40px;
      padding-left: 24px;
      padding-right: 24px;
      @media (min-width: 768px) {
        padding-left: 40px;
        padding-right: 40px;
      }
    }
  }

  .score__explanation {
    margin-top: 40px;
    margin-bottom: 24px;
    p {
      text-align: center;
    }
    .score__title {
      text-align: center;
      font-family: $fRoboto;
      font-weight: $fwMed;
      font-size: 17px;
      line-height: 20px;
      margin-bottom: 16px;
    }
  }

  .el-slider.scorecard {
    .el-slider__runway {
      height: 14px !important;
      border-radius: 13px;
    }
    .el-slider__button-wrapper {
      top: -12px;
      z-index: 9;
    }
    .el-slider__button {
      width: 24px !important;
      height: 24px;
      background: $cOrange;
    }
    .el-slider__runway.disabled .el-slider__button-wrapper:hover {
      cursor: default;
    }
    .el-slider__runway.disabled .el-slider__button {
      border-color:$cOrange;
    }
    .el-slider__runway.disabled .el-slider__button:hover {
      cursor: default;
    }
  }

  .planetary {
    margin-top: 40px;
    margin-bottom: 40px;
    .heading {
      font-family: $fRoboto;
      font-weight: $fwMed;
      font-size: 17px;
      line-height: 20px;
      margin-bottom: 16px;
    }
    .pie-chart {
      margin: 40px auto 40px;
      @media (min-width: 768px) {
        max-width: 340px;
      }
      img {
        width: 100%;
      }
    }
    .chart-sub-label {
      font-family: $fPrimary;
      font-weight: $fwReg;
      font-size: 11px;
      line-height: 14px;
      text-align: center;
      max-width: 215px;
      margin: 25px auto 42px;
      strong {
        font-weight: $fwSemiBold;
      }
    }
  }

  .el-slider__button {
    width: 24px;
    height: 24px;
  }
</style>
